import { Injectable, signal } from "@angular/core";
import { collection, doc, docData, DocumentReference, DocumentSnapshot, Firestore, getDoc, getDocs, limit, onSnapshot, orderBy, query, where } from "@angular/fire/firestore";
import { Data, NavigationEnd, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { AuthStateService } from "@core/auth-state.service";
import { CatchErrorService } from "@core/catch-error.service";
import { ErrorHandlerService } from "@core/ErrorHandlerService/error-handler.service";
import { BehaviorSubject, combineLatest, defer, EMPTY, firstValueFrom, from, interval, Observable, of, Subject, Subscription } from "rxjs";
import {
  catchError,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom
} from "rxjs/operators";
import { CommonVariable } from "src/app/app.constants";
import { CommonService } from "src/app/services/common/common.service";
import { StoreJackpotRepositoryService } from "./store-jackpot.service";
import { StoreSyncService } from "./store-sync.service";
import { StoreFirestoreHelperService } from "./services/store-firestore-helper.service";
import { StoreJackpotTriggerDataService } from "./services/store-jackpot-trigger-data.service";

@Injectable({
  providedIn: "root",
})
export class StoreRepositoryService {
  preferredCurrency: string = CommonVariable.preferredCurrency;
  languageCurrency: string = CommonVariable.language_currency;
  preferredLanguage: string = CommonVariable.preferredLanguage;
  productNameSignal = signal("");
  /** Forwarded from StoreJackpotTriggerDataService so existing consumers keep working. */
  get jackpotActivatedCount$(): BehaviorSubject<number> {
    return this.jackpotTriggerDataService.jackpotActivatedCount$;
  }
  showJackpotPromoSection$ = new BehaviorSubject<any>(false);

  public userOfferCapping$ = new BehaviorSubject<Data>(null);
  iSdiscountAvailable = signal<boolean>(false);
  RapidFire: Data;
  private closeCheckoutModalSubject = new Subject<void>();
  public closeModal$ = this.closeCheckoutModalSubject.asObservable();
  public processedStatus = signal<number>(0);
  public flashSaleBannerExpired$ = new BehaviorSubject<boolean>(false);
  // Helper method to ensure authentication is valid before proceeding with Firestore operations
  private ensureAuthenticated(): Observable<boolean> {
    return this.authService.ensureValidAuth();
  }

  getUserPreferredCurrency$ = this.authService.userDetails$.pipe(
    switchMap((user: Data) => {
      return this.authService.getUid$("StoreRepositoryService").pipe(
        switchMap((userId) => {
          if (!userId) userId = null;
          const userDocRef = doc(this.firestore, `users/${userId}`);
          return from(getDoc(userDocRef)).pipe(
            map((res) => {
              const data = res.data() as Data;
              return data?.preferred_currency ?? CommonVariable.preferredCurrency; // Return default if property doesn't exist
            }),
            catchError(() => {
              return of(CommonVariable.preferredCurrency); // Default value in case of error
            })
          );
        }));

    })
  );
  // slider$ observable was changed to get the slides from the store_promotion/slides collection based on the user's language_currency

  private lastStoreUpdateTimestamp = 0;
  private readonly UPDATE_THROTTLE_MS = 60000; // 1 minute throttle
  private readonly STALE_DATA_THRESHOLD_MS = 3600000; // 1 hour

  slides$ = this.catchService.isOnline$.pipe(
    switchMap((isOnline) => {
      if (!isOnline) return EMPTY;
      return new Observable<Data>((observer) => {
        const docRef = doc(this.firestore, `/Global_lookup/store_promotion/slides/${this.languageCurrency}`);
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
          observer.next(docSnap.exists() ? docSnap.data() as Data : {});
        }, (error) => observer.error(error));
        return () => unsubscribe();
      }).pipe(
        this.catchService.retryWhen,
        this.catchService.catchOfflineError
      );
    }),
    this.catchService.retryWhen,
    this.catchService.catchOfflineError,
    shareReplay(1),
    tap((globalData: Data) => {
      // Fire-and-forget background update - non-blocking
      if (globalData) {
        this.storeSyncService.userMainPage().pipe(
          take(1),
          tap((userMainPageData: Data) => {
            const now = Date.now();

            // Only update if:
            // 1. Data is significantly stale (older than 1 hour)
            // 2. Last update was more than 1 minute ago (throttle)
            if (globalData?.update_timestamp?.toDate && userMainPageData?.update_timestamp?.toDate) {
              const timeDiff = globalData.update_timestamp.toDate().getTime() - userMainPageData.update_timestamp.toDate().getTime();
              const shouldUpdate = timeDiff > this.STALE_DATA_THRESHOLD_MS &&
                (now - this.lastStoreUpdateTimestamp) > this.UPDATE_THROTTLE_MS;

              if (shouldUpdate) {
                this.lastStoreUpdateTimestamp = now;
                // Schedule update in the background without blocking
                setTimeout(() => {
                  this.authService.getUid$("StoreRepositoryService").pipe(
                    take(1),
                    tap((userId) => {
                      if (!userId) return;
                      const req = {
                        userId: userId,
                        page: "store",
                        method: "createStoreBanners",
                      };
                      // Fire-and-forget - don't wait for response
                      this.authService
                        .updateMainPages(req)
                        .pipe(take(1))
                        .subscribe({
                          error: (err) => this.authService.errorHandlerLogs('Background store update failed:', err)
                        });
                    })
                  ).subscribe();
                }, 0);
              }
            }
          })
        ).subscribe();
      }
    })
  )

  offersForYou$ = this.catchService.isOnline$.pipe(
    switchMap((isOnline) => {
      if (isOnline) {
        const documentRef = doc(this.firestore, `/general_modules/fetch_lottery_products_offers_for_you`);
        return new Observable((observer) => {
          const unsubscribe = onSnapshot(
            documentRef,
            (snapshot) => observer.next(snapshot),
            (error) => observer.error(error)
          );
          return () => unsubscribe();
        }).pipe(this.catchService.retryWhen, this.catchService.catchOfflineError);
      } else {
        return of(null);
      }
    }),
    this.catchService.retryWhen,
    this.catchService.catchOfflineError,
    shareReplay(1)
  ).pipe(
    map((res: any) => {
      // Check if res is null/undefined or if it's the offline placeholder
      if (!res || res?.isOfflinePlaceholder) return null;
      // Check if res has a data method (is a DocumentSnapshot)
      if (typeof res?.data === 'function') {
        return res.data();
      }
      return null;
    })
  );

  storeOffer$ = this.catchService.isOnline$.pipe(
    switchMap((isOnline) => {
      if (isOnline) {
        // Use standalone `doc()` to create a document reference
        const documentRef = doc(this.firestore, `/general_modules/store_offers_v1`);
        return new Observable((observer) => {
          const unsubscribe = onSnapshot(
            documentRef,
            (snapshot) => observer.next(snapshot),
            (error) => observer.error(error)
          );
          return () => unsubscribe();
        }).pipe(this.catchService.retryWhen, this.catchService.catchOfflineError);
      } else {
        return of(null);
      }
    }),
    this.catchService.retryWhen,
    this.catchService.catchOfflineError,
    shareReplay(1)
  ).pipe(
    map((res: any) => {
      // Check if res is null/undefined or if it's the offline placeholder
      if (!res || res?.isOfflinePlaceholder) return null;
      // Check if res has a data method (is a DocumentSnapshot)
      if (typeof res?.data === 'function') {
        return res.data();
      }
      return null;
    })
  );
  productList = [
    {
      EuroMillions: "EuroMillions",
      Lotto: "Lotto",
      ThunderBall: "Thunderball",
      PowerBall: "Powerball",
      Megamillions: "Mega Millions",
      Eurojackpot: "Eurojackpot",
      SuperenaLotto: "SuperEnalotto",
      "Irish Lotto": "Irish Lotto",
      FrenchLotto: "FrenchLotto",
      LottoMax: "LottoMax",
      LaPrimitiva: "LaPrimitiva",
      Lotto6aus49: "Lotto6aus49",
    },
  ];
  // ...............................................................
  // Lazy product observables collapsed into a single parameterized, cached
  // accessor. Each known product name resolves to a deferred, shareReplay(1)
  // observable that is created on first request and reused thereafter.
  private readonly knownProductNames = new Set<string>([
    "EuroMillions",
    "Lotto",
    "ThunderBall",
    "PowerBall",
    "Eurojackpot",
    "Megamillions",
    "SuperenaLotto",
    "Irish Lotto",
    "FrenchLotto",
    "LaPrimitiva",
    "LottoMax",
    "Lotto6aus49",
  ]);
  private productObservableCache = new Map<string, Observable<Data>>();

  // .....................................................

  offers$ = this.storeFirestoreHelper.getAngularFireStore("/offer_listing").pipe(shareReplay(1));
  lotteries$ = this.storeFirestoreHelper.getAngularFireStore("/lotteries").pipe(shareReplay(1));

  jackpotOrder$ = this.authService.getAngularFireStore("/Global_lookup/jackpot_order", "get").pipe(
    map((res: Data) => {
      const data = res?.data() as any;
      const id = res?.id;
      return { id, ...data };
    }),
    shareReplay(1)
  );

  discount$ = this.storeFirestoreHelper.getAngularFireStore("/general_modules/system_reference/offer_discount").pipe(shareReplay(1));
  lotteryLines$ = this.authService?.lotteryLInes$;
  storeOffers$ = this.authService
    .getAngularFireStore("/general_modules/store_offers_v1", "snapshotChanges")
    .pipe(map((snapshot: DocumentSnapshot) => snapshot?.data()));


  storePageData$ = combineLatest(this.lotteries$, this.storeJackpotService.jackpots$, this.lotteryLines$, this.jackpotOrder$).pipe(
    filter((res: Data) => res && res?.[2]),
    map((res: any) => {
      if (!res[1] || !res[2]) return;
      const lotteryProducts = [];
      res?.[0]?.map((obj: any) => {
        let data = {} as any;
        data["real_name"] = obj?.real_name;
        data["lotteries"] = obj;
        data["jackpot"] = res?.[1]?.find((x: any) => x?.real_name === obj?.real_name);
        data["jackpot_order"] = res?.[3];
        data.lotteries.line_count = res?.[2]?.find((x: any) => x?.product_name === obj?.real_name)?.line_count;
        const exceptIdAry = [];
        const comingProductId = data["jackpot"]?.coming_product_id;
        const hideProduct = exceptIdAry.includes(comingProductId);
        if (hideProduct === false) lotteryProducts.push(data);
      });
      this.RapidFire = lotteryProducts.find((x) => x?.real_name === "RapidFire");
      return lotteryProducts.filter((x) => parseInt(x?.jackpot?.jackpot_value) > 0 && x?.real_name !== "RapidFire");
    })
  );

  selectedProduct$ = new BehaviorSubject("");

  preferredCurrency$ = this.authService.userDetails$?.subscribe((user: any) => {
    this.preferredCurrency = user?.preferred_currency ?? CommonVariable.preferredCurrency;
    this.languageCurrency = user?.language_currency ?? CommonVariable.language_currency;
    this.preferredLanguage = user?.language_code ?? CommonVariable.preferredLanguage;
  });

  selectedProductData$ = new BehaviorSubject({});
  selectedOfferDataPage$ = this.selectedProduct$?.pipe(
    withLatestFrom(this.selectedProductData$, this.lotteries$, this.storeJackpotService.jackpots$),
    switchMap((product: any) => {
      // Check if product or product[0] is missing and log accordingly
      if (!product || !product[0]) {
        return of(null);
      }
      // Log the transformed product name
      product[0] = product[0]?.real_name ? product[0].real_name : product[0];
      // Check if there is a match in the product array for the product name
      if (product.some((x) => x?.name?.toLowerCase() === product[0]?.toLowerCase())) {
        const matchedProduct = product.find((x) => x?.name?.toLowerCase() === product[0]?.toLowerCase());

        return of({
          ...matchedProduct,
          lottery: product[product.length - 1]?.find((x) => x?.real_name?.toLowerCase() === product[0]?.toLowerCase()),
          jackpot: product[product.length - 2]?.find((x) => x?.real_name?.toLowerCase() === product[0]?.toLowerCase()),
        });
      }

      // Try to get data from the lazy-loaded product observable first
      const productObservable$ = this.getProductObservable(product[0]);

      if (productObservable$) {
        // Use the lazy product observable which leverages fetchstoreOffersv2 with caching
        return productObservable$.pipe(
          map((productData) => {
            if (productData) {
              return {
                ...productData,
                lottery: product[product.length - 1]?.find((x) => x?.real_name?.toLowerCase() === product[0]?.toLowerCase()),
                jackpot: product[product.length - 2]?.find((x) => x?.real_name?.toLowerCase() === product[0]?.toLowerCase()),
              };
            }
            return null;
          }),
          startWith(null)
        );
      }

      // Fallback to direct Firestore queries if product observable not found
      return combineLatest([
        this.storeFirestoreHelper.getAngularFireStore(`/general_modules/store_offers_v1/${product[0]}/2_weeks/offers`),
        this.storeFirestoreHelper.getAngularFireStore(`/general_modules/store_offers_v1/${product[0]}/4_weeks/offers`),
        this.storeFirestoreHelper.getAngularFireStore(`/general_modules/store_offers_v1/${product[0]}/8_weeks/offers`),
        this.storeFirestoreHelper.getAngularFireStore(`/general_modules/store_offers_v1/${product[0]}/credit/offers`),
        this.storeFirestoreHelper.getAngularFireStore(`/general_modules/store_offers_v1/${product[0]}/oneoff/offers`),
        this.storeFirestoreHelper.getAngularFireStore(`/general_modules/store_offers_v1/${product[0]}/subscription/offers`),
      ]).pipe(
        map((products) => {
          // Construct productData with logs for each stage
          const productData: any = {};
          products.forEach((product, index) => {
            try {
              if (index === 0) productData["2_weeks"] = product;
              else if (index === 1) productData["4_weeks"] = product;
              else if (index === 2) productData["8_weeks"] = product;
              else if (index === 3) productData["credit"] = product;
              else if (index === 4) productData["oneoff"] = product;
              else if (index === 5) productData["subscription"] = product;
            } catch (e) {
              this.authService.errorHandlerLogs("Error processing product data at index " + index, e);
            }
          });

          const finalProductData = {
            ...productData,
            lottery: product[product.length - 1]?.find(
              (x) => x?.real_name?.toLowerCase() === product[0]?.toLowerCase()
            ),
            jackpot: product[product.length - 2]?.find(
              (x) => x?.real_name?.toLowerCase() === product[0]?.toLowerCase()
            ),
          };

          return finalProductData;
        }),
        startWith(null) // Initial value to handle undefined scenario
      );
    })
  );

  customOfferCashBackText$ = new BehaviorSubject("");
  customOfferAwardId$ = new BehaviorSubject(0);
  jackpotTriggerExtraSubscription: Subscription;
  selectedOfferDataPageSubscription: Subscription;
  storePromotionSubscription: Subscription;
  topJackpotSubscription: Subscription;
  jackpotTriggerSubscription: Subscription;
  jackpotSubscription: Subscription;
  jackpotTriggerOrderSubscription: Subscription;
  jackpotOrderSubscription: Subscription;
  bottomJackpotSubscription: Subscription;
  flashSaleBannerSubscription: Subscription;
  userBackout$ = new BehaviorSubject(false);

  constructor(
    private firestore: Firestore,
    private authService: AuthService,
    private catchService: CatchErrorService,
    private router: Router,
    private errorHandlerService: ErrorHandlerService,
    private commonService: CommonService,
    private storeJackpotService: StoreJackpotRepositoryService,
    private storeSyncService: StoreSyncService,
    private storeFirestoreHelper: StoreFirestoreHelperService,
    private jackpotTriggerDataService: StoreJackpotTriggerDataService,
    private authStateService: AuthStateService,
  ) {
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      filter(event =>
        event.url.includes('store') ||
        event.url.includes('syndicate') ||
        event.url.includes('subscription-settings')
      ),
      take(1)
    ).subscribe(() => {
      // Ensure user is authenticated before starting subscriptions
      this.ensureAuthenticated().pipe(take(1)).subscribe(isAuthenticated => {
        if (!isAuthenticated) {
          this.authService.errorHandlerLogs("User not authenticated, skipping store repository initialization");
          return;
        }

        this.cleanupSubscriptions(); // Clean up any existing subscriptions before starting new ones

        // Stagger initialization using increasing delays to prevent overwhelming IndexedDB
        setTimeout(() => this.storeJackpotService.getUserJackpotTrigger(), 0);
        setTimeout(() => this.jackpotTriggerExtraSubscription = this.jackpotTriggerDataService.jackpotTrigger$?.subscribe(), 100);
        setTimeout(() => this.selectedOfferDataPageSubscription = this.selectedOfferDataPage$?.pipe(tap()).subscribe(), 200);
        // setTimeout(() => this.storePromotionSubscription = this.updateStorePromotionData$.subscribe(), 300);
        setTimeout(() => this.topJackpotSubscription = this.updateTopJackpotData$.subscribe(), 400);
        setTimeout(() => this.topJackpotSubscription = this.updateLatestJackpotData$.subscribe(), 400);
        setTimeout(() => this.jackpotTriggerSubscription = this.updateJackpotTriggerData$.subscribe(), 500);
        // setTimeout(() => this.jackpotSubscription = this.updateJackpotData$.subscribe(), 600);
        setTimeout(() => this.jackpotTriggerOrderSubscription = this.updateJackpotTriggerOrderData$.subscribe(), 700);
        setTimeout(() => this.jackpotOrderSubscription = this.updateJackpotOrderData$.subscribe(), 800);
        setTimeout(() => this.bottomJackpotSubscription = this.updateBottomJackpotData$.subscribe(), 900);
        setTimeout(() => this.flashSaleBannerSubscription = this.updateGameFlashSaleBanner$.subscribe(), 1000);
      });
    });
  }

  private cleanupSubscriptions() {
    if (this.selectedOfferDataPageSubscription) {
      this.selectedOfferDataPageSubscription.unsubscribe();
      this.selectedOfferDataPageSubscription = null;
    }
    if (this.jackpotTriggerExtraSubscription) {
      this.jackpotTriggerExtraSubscription.unsubscribe();
      this.jackpotTriggerExtraSubscription = null;
    }
    if (this.storePromotionSubscription) {
      this.storePromotionSubscription.unsubscribe();
      this.storePromotionSubscription = null;
    }
    if (this.topJackpotSubscription) {
      this.topJackpotSubscription.unsubscribe();
      this.topJackpotSubscription = null;
    }
    if (this.jackpotTriggerSubscription) {
      this.jackpotTriggerSubscription.unsubscribe();
      this.jackpotTriggerSubscription = null;
    }
    if (this.jackpotSubscription) {
      this.jackpotSubscription.unsubscribe();
      this.jackpotSubscription = null;
    }
    if (this.jackpotTriggerOrderSubscription) {
      this.jackpotTriggerOrderSubscription.unsubscribe();
      this.jackpotTriggerOrderSubscription = null;
    }
    if (this.jackpotOrderSubscription) {
      this.jackpotOrderSubscription.unsubscribe();
      this.jackpotOrderSubscription = null;
    }
    if (this.bottomJackpotSubscription) {
      this.bottomJackpotSubscription.unsubscribe();
      this.bottomJackpotSubscription = null;
    }
    if (this.flashSaleBannerSubscription) {
      this.flashSaleBannerSubscription.unsubscribe();
      this.flashSaleBannerSubscription = null;
    }
  }

  preloadTopLotteries(preloadTopLotteries: any[]) {
    // Only preload the first lottery to reduce initial load
    // Other lotteries will be loaded on-demand when user navigates to them
    if (!preloadTopLotteries || preloadTopLotteries.length === 0) return;

    const firstLottery = preloadTopLotteries[0];
    if (!firstLottery?.real_name) return;

    this.selectedProduct$.next(firstLottery.real_name);

    // Get the observable for the first lottery product
    const productObservable$ = this.getProductObservable(firstLottery.real_name);

    if (productObservable$) {
      productObservable$.pipe(take(1)).subscribe((data) => {
        this.selectedProductData$.next(data);
      });
    }
  }

  // Parameterized, cached accessor that replaces the 12 individually named
  // product observables. Returns a lazily-created, shareReplay(1) observable
  // per known product name (cached in a Map) or null for unknown names.
  private getProductObservable(productName: string): Observable<Data> | null {
    if (!productName || !this.knownProductNames.has(productName)) {
      return null;
    }

    let cached = this.productObservableCache.get(productName);
    if (!cached) {
      cached = defer(() => this.fetchstoreOffersv2(productName)).pipe(shareReplay(1));
      this.productObservableCache.set(productName, cached);
    }
    return cached;
  }

  getSyndicateRequestId(syndicateRequestId: string) {
    return this.authService.getUid$("StoreRepositoryService").pipe(
      switchMap((userId) => {
        if (!userId || !syndicateRequestId) return of([]);
        const docRef = doc(this.firestore, `users/${userId}/syndicate_request/${syndicateRequestId}`);

        return from(getDoc(docRef)).pipe(
          map((docSnap) => docSnap.exists() ? docSnap.data() : null),
          shareReplay(1)
        );
      }));
  }
  /**
   * The function `updatePreviousPageFromServer` updates the previous page route for a user in Firestore
   * based on the provided page name.
   */

  updatePreviousPageFromServer(pageName: string) {
    return this.commonService.getUid("StoreRepositoryService").pipe(
      switchMap((userId) => {
        if (userId === null || !userId) return of(null);
        const path_id = "yzF1Ss5eiMqnh91zQMtv";
        const doc_data = { storePagePreviousRoute: pageName };
        const method = "update";

        this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          method,
        }).subscribe({
          error: (error) => {
            this.authService.errorHandlerLogs("Error updating previous page route:", error);
            return error;
          }
        });
        return of(userId) || of(null);
      }));
  }

  /**
   * The function `getPreviousPageFromServer` retrieves the previous page route for a user from the
   * server, defaulting to a store tab URL if the user is not logged in.
   */
  getPreviousPageFromServer(): Observable<string> {
    const storeTabUrl = "/mainTabs/tabs/store";
    return this.authService.getUid$("StoreRepositoryService").pipe(
      switchMap((userId) => {
        if (userId === null) {
          return of(storeTabUrl);
        }

        const userDocRef = doc(this.firestore, `users/${userId}`); // Create a reference to the user's document

        return from(getDoc(userDocRef)).pipe( // Use getDoc() to fetch the document
          map((value: any) => {
            return value?.data()?.storePagePreviousRoute || storeTabUrl;
          }),
          catchError(() => {
            return of(storeTabUrl); // Return default URL on error
          })
        );
      }));
  }

  // This function is used to get track changes in managed tickets of the user
  public trackUserManagedTickets(): Observable<any> {
    try {
      return this.authService.getUid$("StoreRepositoryService").pipe(
        switchMap((userId) => {
          if (!userId) userId = null;
          return this.catchService.isOnline$.pipe(
            switchMap((isOnline) => {
              if (isOnline) {
                // Create a reference to the collection
                const collectionRef = collection(this.firestore, `/users/${userId}/managed_tickets`);

                // Use onSnapshot wrapped in an Observable to replicate snapshotChanges()
                return new Observable((observer) => {
                  const unsubscribe = onSnapshot(
                    collectionRef,
                    (snapshot) => {
                      // Filter out null/undefined documents before creating actions
                      const actions = snapshot.docs
                        .filter((doc) => doc && typeof doc.data === 'function')
                        .map((doc) => ({
                          payload: { doc }, // Mimic snapshotChanges() structure
                          type: "added", // You can customize this type based on your app logic
                        }));
                      observer.next(actions); // Emit actions mimicking DocumentChangeAction[]
                    },
                    (error) => observer.error(error) // Handle Firestore errors
                  );
                  return () => unsubscribe(); // Cleanup on unsubscription
                }).pipe(this.catchService.retryWhen, this.catchService.catchOfflineError); // Retry logic and error handling
              } else {
                return EMPTY; // Return EMPTY when offline
              }
            }),
            this.catchService.retryWhen,
            this.catchService.catchOfflineError,
            shareReplay(1) // Share and replay the data stream
          ).pipe(
            map((actions: any[]) => this.storeFirestoreHelper.snapShotData(actions)) // Use snapShotData to process actions
          );
        }));

    } catch (e) {
      this.authService.errorHandlerLogs("Error in snapShotData processing", e);
      throw e; // Re-throw the error for proper handling
    }
  }

  public triggerCloseModal(): void {
    this.closeCheckoutModalSubject.next();
  }
  // Retool First card
  Sample$ = this.catchService.isOnline$.pipe(
    switchMap((isOnline) => {
      if (isOnline) {
        // Create a reference to the document
        const documentRef = doc(this.firestore, `/Global_lookup/store_promotion/special_promotion/slider_1`);

        // Wrap onSnapshot inside a custom Observable to replicate snapshotChanges()
        return new Observable((observer) => {
          const unsubscribe = onSnapshot(
            documentRef,
            (snapshot) => observer.next(snapshot), // Pass real-time document snapshot
            (error) => observer.error(error) // Pass errors to the observer
          );
          return () => unsubscribe(); // Cleanup on unsubscribe
        }).pipe(this.catchService.retryWhen, this.catchService.catchOfflineError); // Retry and error handling
      } else {
        return EMPTY; // Return EMPTY if offline
      }
    }),
    this.catchService.retryWhen,
    this.catchService.catchOfflineError
  ).pipe(
    map((res: any) => {
      // Check if res is null/undefined or if it's the offline placeholder
      if (!res || res?.isOfflinePlaceholder) return null;
      // Check if res has a data method (is a DocumentSnapshot)
      if (typeof res?.data === 'function') {
        return res.data();
      }
      return null;
    })
  );



  updateStorePromotionData$: Observable<void> = this.storeSyncService.createSyncListener(`/Global_lookup/store_promotion/slides/${this.languageCurrency}`, "createStoreBanners");

  updateLatestJackpotData$: Observable<void> = this.catchService.isOnline$.pipe(
    switchMap((isOnline) => {
      if (!isOnline) return EMPTY;

      return new Observable<Data[]>((observer) => {
        const q = query(
          collection(this.firestore, "/jackpot"),
          orderBy("update_timestamp", "desc"),
          limit(1)
        );

        const unsubscribe = onSnapshot(
          q,
          (querySnapshot) => {
            observer.next(querySnapshot.docs.map((doc) => doc.data()));
          },
          (error) => observer.error(error)
        );

        return () => unsubscribe();
      }).pipe(
        this.catchService.retryWhen,
        this.catchService.catchOfflineError
      );
    }),

    this.catchService.retryWhen,
    this.catchService.catchOfflineError,

    switchMap((globalDataArray: Data[]) => {
      const globalData = globalDataArray?.[0];
      if (!globalData?.real_name) return EMPTY;

      return this.authService.getUid$("StoreRepositoryService").pipe(
        take(1),
        tap((userId) => {
          if (!userId) return;

          const path_id = "UFVkdSH1l4iprbJPL0EP";
          const dv = { docId: globalData?.real_name };
          const doc_data = {
            expiry_date: globalData?.expiry_date?.toDate(),
            jackpot_value: globalData?.jackpot_value,
            update_timestamp: new Date(),
          };
          const method = "update";

          this.authService
            .getV2CloudFunction("updatefirestoredoc", {
              path_id,
              dv,
              doc_data,
              method,
            })
            .pipe(take(1))
            .subscribe({
              error: (error) =>
                this.authService.errorHandlerLogs("Jackpot update failed:", error),
            });
        })
      );
    }),

    map(() => undefined)
  );

  updateTopJackpotData$: Observable<void> = this.storeSyncService.createSyncListener("/jackpot", "createTopJackpots", true);

  updateJackpotTriggerData$: Observable<void> = this.storeSyncService.createSyncListener("/jackpot_trigger", "createJackpotTriggers", true);

  updateJackpotData$: Observable<void> = this.storeSyncService.createSyncListener("/jackpot", "createJackpotTriggers", true);

  updateJackpotTriggerOrderData$: Observable<void> = this.storeSyncService.createSyncListener("Global_lookup/jackpot_trigger_order", "createJackpotTriggers");

  updateJackpotOrderData$: Observable<void> = this.storeSyncService.createSyncListener("/Global_lookup/jackpot_order", "createBottomJackpots");

  private lastBottomJackpotUpdateTimestamp = 0;

  updateBottomJackpotData$: Observable<void> = combineLatest([
    this.catchService.isOnline$,
    this.authService.userDetails$
  ]).pipe(
    switchMap(([isOnline, userDetails]) => {
      if (!isOnline) return EMPTY;

      // Get language code from userDetails with fallback to default
      const languageCode = userDetails?.language_code || CommonVariable.preferredLanguage;

      // Ensure we have a valid language code
      if (!languageCode || languageCode.trim() === '') {
        this.authService.errorHandlerLogs('Invalid language code for bottom jackpot data, using default');
        return EMPTY;
      }

      return new Observable<Data>((observer) => {
        const docRef = doc(this.firestore, `translations_main_tabs/${languageCode}`);
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
          observer.next(docSnap.exists() ? docSnap.data() as Data : {});
        }, (error) => observer.error(error));
        return () => unsubscribe();
      }).pipe(
        this.catchService.retryWhen,
        this.catchService.catchOfflineError
      );
    }),
    this.catchService.retryWhen,
    this.catchService.catchOfflineError,
    shareReplay(1),
    tap((globalData: Data) => {
      // Fire-and-forget background update - non-blocking
      if (globalData) {
        this.storeSyncService.userMainPage().pipe(
          take(1),
          tap((userMainPageData: Data) => {
            const now = Date.now();

            // Only update if data is significantly stale and throttle is met
            if (globalData?.update_timestamp?.toDate && userMainPageData?.update_timestamp?.toDate) {
              const timeDiff = globalData.update_timestamp.toDate().getTime() - userMainPageData.update_timestamp.toDate().getTime();
              const shouldUpdate = timeDiff > this.STALE_DATA_THRESHOLD_MS &&
                (now - this.lastBottomJackpotUpdateTimestamp) > this.UPDATE_THROTTLE_MS;

              if (shouldUpdate) {
                this.lastBottomJackpotUpdateTimestamp = now;
                // Schedule update in the background without blocking
                setTimeout(() => {
                  this.authService.getUid$("StoreRepositoryService").pipe(
                    take(1),
                    tap((userId) => {
                      if (!userId) return;
                      const req = {
                        userId: userId,
                        page: "store",
                        method: "createBottomJackpots",
                      };
                      // Fire-and-forget - don't wait for response
                      this.authService
                        .updateMainPages(req)
                        .pipe(take(1))
                        .subscribe({
                          error: (err) => this.authService.errorHandlerLogs('Background bottom jackpot update failed:', err)
                        });
                    })
                  ).subscribe();
                }, 0);
              }
            }
          })
        ).subscribe();
      }
    }),
    map(() => undefined) // Return void to match Observable<void> signature
  )

  updateGameFlashSaleBanner$: Observable<void> = this.catchService.isOnline$
    .pipe(
      switchMap((isOnline) => {
        if (isOnline) {
          const docRef = doc(this.firestore, '/login_gift/login_gift_flash_sale');
          return from(getDoc(docRef)).pipe(
            switchMap((docSnapshot) => {
              const globalData = docSnapshot.data();
              return of(globalData); // Return the document data
            }))
        } else {
          return EMPTY;
        }
      }),
      this.catchService.retryWhen,
      this.catchService.catchOfflineError,
      shareReplay(1)
    )
    .pipe(
      tap((globalData: Data) => {
        if (globalData) {
          this.authService.getUid$("StoreRepositoryService").pipe(
            take(1),
            switchMap((userId) => {
              if (!userId) return of(null);
              if (globalData?.status === true && globalData?.expiry_date?.toDate() > new Date()) {
                this.authService.processLoginGift(userId);
              }
              return of(null);
            })).subscribe();
        }
      }),
      map(() => undefined)
    );

  getCardOfferDetails(language_currency: string): Promise<Data> {
    if (!language_currency) {
      return Promise.reject(new Error("Language currency is not provided."));
    }

    const path_id = "lAb2jHlng7s6VSWwzyHN"; // static path_id you had
    const dv = { docId: language_currency }; // docId based on language_currency
    const method = "read";

    return new Promise((resolve, reject) => {
      this.authService
        ?.getV2CloudFunction("readfirestoredoc", { path_id, dv, method })
        .pipe(
          map((snapshot: any) => snapshot?.data) // extract data
        )
        .subscribe({
          next: (data) => {
            resolve(data);
          },
          error: (error) => {
            this.authService.errorHandlerLogs("Error in getCardOfferDetails:", error);
            reject(error);
          }
        });
    });
  }

  ngOnDestroy() {
    if (this.storePromotionSubscription) this.storePromotionSubscription?.unsubscribe();
    if (this.topJackpotSubscription) this.topJackpotSubscription?.unsubscribe();
    if (this.jackpotTriggerSubscription) this.jackpotTriggerSubscription?.unsubscribe();
    if (this.jackpotSubscription) this.jackpotSubscription?.unsubscribe();
    if (this.jackpotTriggerOrderSubscription) this.jackpotTriggerOrderSubscription?.unsubscribe();
    if (this.jackpotOrderSubscription) this.jackpotOrderSubscription?.unsubscribe();
    if (this.bottomJackpotSubscription) this.bottomJackpotSubscription?.unsubscribe();
    if (this.flashSaleBannerSubscription) this.flashSaleBannerSubscription?.unsubscribe();
  }
  storeData$ = this.catchService.isOnline$.pipe(
    switchMap((isOnline) => {
      if (isOnline) {
        // Create a reference to the document
        return this.authService.getUid$("StoreRepositoryService").pipe(
          switchMap((userId) => {
            if (!userId) {
              return of(null);
            }
            const documentRef = doc(this.firestore, `/users/${userId}/main_pages/store`);
            // Defense-in-depth gate for user-private store page (LOTTOSOCIAL-PWA-1MEE)
            return this.authStateService.customTokenReady$.pipe(
              take(1),
              switchMap(() => new Observable((observer) => {
                const unsubscribe = onSnapshot(
                  documentRef,
                  (snapshot) => observer.next(snapshot), // Emit the snapshot
                  (error) => observer.error(error) // Handle Firestore errors
                );
                return () => unsubscribe(); // Cleanup on unsubscription
              }).pipe(
                this.catchService.retryWhen,
                this.catchService.catchOfflineError,
                map((res: Data) => {
                  // Check if res is null/undefined or if it's the offline placeholder
                  if (!res || res?.isOfflinePlaceholder) return null;
                  // Check if res has a data method (is a DocumentSnapshot)
                  if (typeof res?.data === 'function') {
                    return res.data();
                  }
                  return null;
                })
              ))
            );
          }));
      } else {
        // Handle offline scenario or return an empty observable
        return of(null); // Replace with appropriate handling if needed
      }
    }),
    this.catchService.retryWhen,
    this.catchService.catchOfflineError
  );

  homeScrollLotteriesData$ = this.catchService.isOnline$.pipe(
    switchMap((isOnline) => {
      if (isOnline) {
        return this.authService.getUid$("StoreRepositoryService").pipe(
          switchMap((userId) => {
            if (!userId) {
              return of(null);
            }
            const documentRef = doc(this.firestore, `/users/${userId}/main_pages/home_scroll_lottery_preferences`);
            return new Observable((observer) => {
              const unsubscribe = onSnapshot(
                documentRef,
                (snapshot) => observer.next(snapshot),
                (error) => observer.error(error)
              );
              return () => unsubscribe();
            }).pipe(
              this.catchService.retryWhen,
              this.catchService.catchOfflineError,
              map((res: Data) => {
                if (!res || res?.isOfflinePlaceholder) return null;
                if (typeof res?.data === 'function') {
                  return res.data();
                }
                return null;
              })
            );
          }));
      } else {
        return of(null);
      }
    }),
    this.catchService.retryWhen,
    this.catchService.catchOfflineError,
    shareReplay({ bufferSize: 1, refCount: true })
  );

  // This Method is used to get User Jackpot Trigger
  getUserOfferCapping() {
    return this.authService.getUid$("StoreRepositoryService").pipe(
      switchMap((userId) => {
        if (userId === null || !userId) return of(null);
        this.storeFirestoreHelper.getAngularFireStore(`/users/${userId}/offer_capping`).subscribe((res: Data) => {
          const storeCreditData = res.find((item) => item.id === "store_credit");
          const ticketCapping = storeCreditData?.ticket_capping;
          this.userOfferCapping$.next(ticketCapping);
        });
        return of(userId) || of(null);
      }));
  }

  getXpsDetailsData$ = this.authService.getUid$("StoreRepositoryService").pipe(
    switchMap(userId => {
      if (!userId) {
        return of(null); // or EMPTY if you want no emission
      }
      return this.authService
        .getAngularFireStore(
          `users/${userId}/gamification/celebrations/xp/details`,
          "snapshotChanges"
        )
        .pipe(
          map((res: Data) => {
            const data = res?.payload?.data();
            return data;
          })
        );
    })
  );

  // Fetch Store Offers datafrom the CF 
  fetchstoreOffersv2(productName: string) {
    const userId = this.authService.userIdSignal();
    // Check if user ID is available
    if (!userId) {
      this.errorHandlerService.logError('selectedlanguage$ failed');
      return of(null); // return observable instead of Promise
    }
    // Prepare the payload
    const payload = {
      user_id: userId,
      product_name: productName,
      adaptiveOutput: true
    };

    return this.authService.getV2CloudFunction("fetchstoreoffersv2", payload).pipe(
      switchMap((result: Data) => {
        // Cf failure case
        if (result?.status === "failed" || !result) return of(null);
        // Cf success case
        if (result?.status === "success") {
          const docRef = doc(this.firestore, `/users/${userId}/store_product/${productName}`);
          return this.catchService.isOnline$.pipe(
            switchMap((isOnline) => {
              if (isOnline) {
                return new Observable((observer) => {
                  const unsubscribe = onSnapshot(
                    docRef,
                    (snapshot) => observer.next(snapshot),
                    (error) => observer.error(error)
                  );
                  return () => unsubscribe();
                }).pipe(this.catchService.retryWhen, this.catchService.catchOfflineError);
              } else {
                return of(null);
              }
            }),
            map((res: Data) => {
              // Check if res is null/undefined or if it's the offline placeholder
              if (!res || res?.isOfflinePlaceholder) return null;
              // Check if res has a data method (is a DocumentSnapshot)
              if (typeof res?.data === 'function') {
                return res.data();
              }
              return null;
            }),
            shareReplay(1),
            take(1)
          );
        } else {
          this.authService.errorHandlerLogs("Cloud function did not return expected result");
          return of(null);
        }
      }),
      catchError((error) => {
        this.authService.errorHandlerLogs("Error fetching store offers:", error);
        return of(null);
      })
    )
  }
  userId$ = interval(100).pipe(
    switchMap(() => this.authService.getUid$("StoreRepositoryService")),
    filter((id): id is string => id !== null), // filter out nulls
    take(1),
    shareReplay(1)
  );

  slogans$ = this.catchService.isOnline$.pipe(
    switchMap((isOnline) => {
      if (!isOnline) return EMPTY;

      return new Observable<{ slogans: Data[] }>((observer) => {
        const docRef = doc(
          this.firestore,
          '/Global_lookup/store_lottery_slogan'
        );

        const unsubscribe = onSnapshot(
          docRef,
          (docSnap) => {
            if (observer.closed) {
              return;
            }

            const data = docSnap.exists() ? docSnap.data() : {};
            const slogans = data?.slogans || [];
            const now = new Date();

            const validSlogans = slogans.filter((s: Data) => {
              const exp = s?.config?.expiry_date;
              return exp && new Date(exp) > now;
            });

            observer.next({ slogans: validSlogans });
          },
          (error) => {
            if (!observer.closed) {
              observer.error(error);
            }
          }
        );
        return () => {
          unsubscribe();
        };
      }).pipe(
        this.catchService.retryWhen,
        this.catchService.catchOfflineError
      );
    }),
    this.catchService.retryWhen,
    this.catchService.catchOfflineError,
    shareReplay({ bufferSize: 1, refCount: true })
  );
  // This method is used to resolve the offer ID from the purchase detail
  resolveOfferCappingOfferId(purchaseDetail: Data): string {
    return String(
      purchaseDetail?.offers?.offerid ??
      purchaseDetail?.offer_id ??
      purchaseDetail?.exit_offer_id ??
      ""
    );
  }

  async validateOfferCappingBeforePurchase(purchaseDetail: Data, purchasePath: string): Promise<boolean> {
    if (!purchaseDetail || purchaseDetail?.isTopUp || purchaseDetail?.isCreatingPS) {
      return false;
    }

    const offerId = this.resolveOfferCappingOfferId(purchaseDetail);
    if (!offerId) {
      return false;
    }

    let userId = "";
    try {
      userId = await this.authService.getUid("StoreRepositoryService");
    } catch (error) {
      this.authService.errorHandlerLogs("[OfferCapping] Failed to resolve user before purchase validation", error);
      return false;
    }

    if (!userId) {
      return false;
    }

    const isCapped = await this.getUserOfferCappingStatus(offerId, userId);
    const timestamp = new Date().toISOString();

    this.logOfferCappingDecision({
      userId,
      offerId,
      capState: isCapped ? "capped" : "clear",
      purchasePath,
      timestamp,
    });

    if (!isCapped) {
      return false;
    }

    await this.showOfferCappingBlockedToast();
    return true;
  }

  private async getUserOfferCappingStatus(offerId: string, userId?: string): Promise<boolean> {
    if (!offerId) {
      return false;
    }

    try {
      const resolvedUserId = userId || await this.authService.getUid("StoreRepositoryService");
      if (!resolvedUserId) {
        return false;
      }

      const offerCappingPath = `users/${resolvedUserId}/offer_capping/${offerId}`;
      const snap = await getDoc(doc(this.firestore, offerCappingPath));
      if (!snap.exists()) {
        return false;
      }

      const data = snap.data();
      return data?.capping_status ?? false;

    } catch (error) {
      this.authService.errorHandlerLogs("[OfferCapping] Error fetching offer capping", error);
      return false;
    }
  }

  private logOfferCappingDecision(decision: {
    userId: string;
    offerId: string;
    capState: string;
    purchasePath: string;
    timestamp: string;
  }): void {
    this.authService.errorHandlerLogs("[OfferCapping] Purchase validation decision", decision);
  }

  private async showOfferCappingBlockedToast(): Promise<void> {
    const defaultMsg = {
      message: {
        line_1: "D-1165-This offer is no longer available because you've reached the purchase limit.",
        line_2: "",
      },
      style: {
        icon: "tick circle error.svg",
        type: "Error",
      },
    };

    try {
      const translatedMsg = await this.authService.getConstantsMsg("1165");
      this.authService.showToastMessage(translatedMsg, defaultMsg);
    } catch (error) {
      this.authService.errorHandlerLogs("[OfferCapping] Error fetching offer capping toast message", error);
      this.authService.showToastMessage({}, defaultMsg);
    }
  }
  //  ================================ End of Offer Capping ================================

}
